import { createLazyFileRoute } from "@tanstack/react-router";
import { FileSpreadsheetIcon, FileTextIcon } from "lucide-react";

import { trpc } from "@blis/trpc/react";

import { ErrorPage } from "@/components/ErrorPage";
import { LoadingPage } from "@/components/LoadingPage";
import { buttonVariants } from "@/components/ui/button";
import { BillingDetailShell } from "@/features/admin/components/billing/BillingDetailShell";
import { BillingRecordNotFound } from "@/features/admin/components/billing/BillingRecordNotFound";
import { InvoicePreview } from "@/features/admin/components/billing/InvoicePreview";
import { getApiUrl } from "@/lib/api";
import { getTrpcErrorCode } from "@/utils/trpc";

export const Route = createLazyFileRoute("/_auth/admin/billing/invoices/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();

  const { data, error, isPending, isError } =
    trpc.admin.billing.getInvoice.useQuery(
      { id },
      {
        retry(failureCount, err) {
          if (getTrpcErrorCode(err) === "NOT_FOUND") {
            return false;
          }
          return failureCount < 3;
        },
      },
    );

  if (isPending) {
    return (
      <BillingDetailShell
        billingTab="invoices"
        breadcrumbTail={`Invoice ${id}`}
        title={`Invoice ${id}`}
      >
        <LoadingPage />
      </BillingDetailShell>
    );
  }

  if (isError && getTrpcErrorCode(error) === "NOT_FOUND") {
    return (
      <BillingDetailShell
        billingTab="invoices"
        breadcrumbTail={`Invoice ${id}`}
        title="Invoice not found"
      >
        <BillingRecordNotFound
          resourceLabel="Invoice"
          resourceId={id}
          billingTab="invoices"
        />
      </BillingDetailShell>
    );
  }

  if (isError) {
    return (
      <BillingDetailShell
        billingTab="invoices"
        breadcrumbTail={`Invoice ${id}`}
        title="Unable to load invoice"
      >
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-lg border border-destructive/25 bg-destructive/5 shadow-xs">
          <ErrorPage error={error} />
        </div>
      </BillingDetailShell>
    );
  }

  return (
    <BillingDetailShell
      billingTab="invoices"
      breadcrumbTail={`Invoice ${data.id}`}
      title={`Invoice ${data.id}`}
      actions={
        <div className="flex items-center gap-2">
          <a
            href={getApiUrl(`/api/invoice/${data.id}?type=pdf`)}
            className={buttonVariants({ variant: "outline", size: "sm" })}
            target="_blank"
            rel="noreferrer"
          >
            <FileTextIcon />
            PDF
          </a>
          <a
            href={getApiUrl(`/api/invoice/${data.id}?type=excel`)}
            className={buttonVariants({ variant: "outline", size: "sm" })}
            target="_blank"
            rel="noreferrer"
          >
            <FileSpreadsheetIcon />
            Excel
          </a>
        </div>
      }
    >
      <InvoicePreview invoice={data} />
    </BillingDetailShell>
  );
}
